import React, { useMemo, useState, useContext } from 'react';
import { compareData, RoundTable } from './dataComparison';
import { AuthContext } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import { transformExcelRowToRoundTable, extractRTNumberFromSubdomain } from './excelToRoundTableTransform';

interface DataComparisonCardProps {
    excelData: Record<string, any>[];
    roundTablesData: RoundTable[];
    onDataUpdated?: () => void;
}

const DataComparisonCard: React.FC<DataComparisonCardProps> = ({ 
    excelData, 
    roundTablesData,
    onDataUpdated 
}) => {
    const authContext = useContext(AuthContext);
    const [isAccepting, setIsAccepting] = useState(false);
    const [acceptProgress, setAcceptProgress] = useState({ current: 0, total: 0 });

    if (!authContext) {
        throw new Error('DataComparisonCard must be used within an AuthProvider');
    }

    const { token } = authContext;

    // Identify duplicate RT numbers in Excel data
    const duplicateRTNumbers = useMemo(() => {
        const rtNumberMap = new Map<number, number>();
        const duplicates = new Set<number>();
        
        excelData.forEach((row) => {
            const subdomain = row['[Club] Subdomain'];
            const rtNumber = extractRTNumberFromSubdomain(subdomain);
            
            if (rtNumber !== null) {
                const count = rtNumberMap.get(rtNumber) || 0;
                rtNumberMap.set(rtNumber, count + 1);
                
                if (count > 0) {
                    duplicates.add(rtNumber);
                }
            }
        });
        
        return duplicates;
    }, [excelData]);

    const comparisonResult = useMemo(() => {
        // Filter out rows with duplicate RT numbers before comparison
        const filteredExcelData = excelData.filter((row) => {
            const subdomain = row['[Club] Subdomain'];
            const rtNumber = extractRTNumberFromSubdomain(subdomain);
            return rtNumber === null || !duplicateRTNumbers.has(rtNumber);
        });
        
        return compareData(filteredExcelData, roundTablesData);
    }, [excelData, roundTablesData, duplicateRTNumbers]);

    const { newEntries, deviatingEntries } = comparisonResult;

    const handleAcceptNewEntries = async () => {
        if (newEntries.length === 0 || isAccepting) {
            return;
        }

        setIsAccepting(true);
        setAcceptProgress({ current: 0, total: newEntries.length });

        try {
            // Create entries one by one (sequentially)
            for (let i = 0; i < newEntries.length; i++) {
                const entry = newEntries[i];
                
                // Update progress
                setAcceptProgress({ current: i + 1, total: newEntries.length });

                // Transform Excel row to RoundTable model
                const requestBody = transformExcelRowToRoundTable(entry.excelRow);

                const response = await fetch('/api/roundtables', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(requestBody),
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || `Fehler beim Erstellen von "${requestBody.name}"`);
                }
            }
            
            toast.success(`${newEntries.length} neue Einträge erfolgreich übernommen`);
            
            // Trigger data reload
            if (onDataUpdated) {
                onDataUpdated();
            }
        } catch (error) {
            console.error('Error accepting new entries:', error);
            toast.error(error instanceof Error ? error.message : 'Fehler beim Übernehmen der neuen Einträge');
        } finally {
            setIsAccepting(false);
            setAcceptProgress({ current: 0, total: 0 });
        }
    };

    const handleAcceptDeviatingEntries = async () => {
        if (deviatingEntries.length === 0 || isAccepting) {
            return;
        }

        setIsAccepting(true);
        setAcceptProgress({ current: 0, total: deviatingEntries.length });

        try {
            // Update entries one by one (sequentially)
            for (let i = 0; i < deviatingEntries.length; i++) {
                const entry = deviatingEntries[i];
                
                // Update progress
                setAcceptProgress({ current: i + 1, total: deviatingEntries.length });

                // Transform Excel row to RoundTable model
                const requestBody = transformExcelRowToRoundTable(entry.excelRow);

                // Use the matched RoundTable ID for updating
                if (!entry.matchedRoundTable) {
                    throw new Error(`Kein übereinstimmender Eintrag gefunden für "${requestBody.name}"`);
                }

                const response = await fetch(`/api/roundtables/${entry.matchedRoundTable.id}`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(requestBody),
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || `Fehler beim Aktualisieren von "${requestBody.name}"`);
                }
            }
            
            toast.success(`${deviatingEntries.length} Einträge erfolgreich aktualisiert`);
            
            // Trigger data reload
            if (onDataUpdated) {
                onDataUpdated();
            }
        } catch (error) {
            console.error('Error accepting deviating entries:', error);
            toast.error(error instanceof Error ? error.message : 'Fehler beim Aktualisieren der Einträge');
        } finally {
            setIsAccepting(false);
            setAcceptProgress({ current: 0, total: 0 });
        }
    };

    // If there are no new or deviating entries, don't show the card
    if (newEntries.length === 0 && deviatingEntries.length === 0) {
        return (
            <div className="data-table-card">
                <div className="table-header">
                    <h3>Datenvergleich</h3>
                </div>
                <div className="content-success">
                    <p>✓ Alle Daten sind mit den aktuellen Einträgen identisch. Keine Abweichungen gefunden.</p>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Full Screen Progress Overlay */}
            {isAccepting && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.85)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999,
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '3rem',
                        borderRadius: '12px',
                        textAlign: 'center',
                        minWidth: '400px',
                        maxWidth: '500px',
                        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
                    }}>
                        <h3 style={{ marginTop: 0, marginBottom: '1.5rem', fontSize: '1.5rem' }}>
                            Einträge werden verarbeitet...
                        </h3>
                        <div style={{ 
                            fontSize: '2rem', 
                            fontWeight: 'bold', 
                            color: '#4CAF50',
                            marginBottom: '1.5rem'
                        }}>
                            {acceptProgress.current} / {acceptProgress.total}
                        </div>
                        <div style={{
                            width: '100%',
                            height: '24px',
                            backgroundColor: '#e0e0e0',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            marginBottom: '1.5rem',
                        }}>
                            <div style={{
                                width: `${(acceptProgress.current / acceptProgress.total) * 100}%`,
                                height: '100%',
                                backgroundColor: '#4CAF50',
                                transition: 'width 0.3s ease',
                            }} />
                        </div>
                        <p style={{ 
                            marginTop: 0, 
                            marginBottom: 0, 
                            color: '#666',
                            fontSize: '1rem'
                        }}>
                            Bitte warten Sie, während die Einträge verarbeitet werden...<br />
                            <strong>Schließen Sie dieses Fenster nicht.</strong>
                        </p>
                    </div>
                </div>
            )}
            
            <div className="data-table-card">

            <div className="table-header">
                <h3>Datenvergleich</h3>
                <div className="table-info">
                    {newEntries.length > 0 && <span>{newEntries.length} neue Einträge</span>}
                    {newEntries.length > 0 && deviatingEntries.length > 0 && <span> • </span>}
                    {deviatingEntries.length > 0 && <span>{deviatingEntries.length} abweichende Einträge</span>}
                    {duplicateRTNumbers.size > 0 && (
                        <>
                            <span> • </span>
                            <span style={{ color: '#dc3545', fontWeight: 'bold' }}>
                                {duplicateRTNumbers.size} doppelte Tischnummern ausgeschlossen
                            </span>
                        </>
                    )}
                </div>
            </div>

            {/* Duplicate Notice */}
            {duplicateRTNumbers.size > 0 && (
                <div style={{
                    padding: '1rem',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '4px',
                    marginBottom: '1rem',
                    color: '#856404'
                }}>
                    <strong>⚠️ Hinweis:</strong> Einträge mit doppelten Tischnummern ({Array.from(duplicateRTNumbers).join(', ')}) wurden vom Vergleich ausgeschlossen. 
                    Bitte beheben Sie die Duplikate in der Excel-Datei, bevor Sie fortfahren.
                </div>
            )}

            {/* New Entries Section */}
            {newEntries.length > 0 && (
                <div className="comparison-section">
                    <div className="comparison-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h4 className="comparison-section-title" style={{ margin: 0 }}>Neue Einträge ({newEntries.length})</h4>
                        <button 
                            className="accept-new-entries-btn"
                            onClick={handleAcceptNewEntries}
                            disabled={isAccepting}
                            style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: '#4CAF50',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: isAccepting ? 'not-allowed' : 'pointer',
                                fontWeight: 'bold',
                                opacity: isAccepting ? 0.6 : 1,
                            }}
                        >
                            {isAccepting ? 'Wird übernommen...' : 'Neue Einträge übernehmen'}
                        </button>
                    </div>
                    <div className="table-container">
                        <table className="data-table comparison-table">
                            <thead>
                                <tr>
                                    <th>Zeile</th>
                                    <th>[Club] Name</th>
                                    <th>[Club] Subdomain</th>
                                    <th>RT-Nummer</th>
                                    <th>[Bank Account] IBAN</th>
                                    <th>[Bank Account] BIC</th>
                                    <th>[Treasurer] Email</th>
                                </tr>
                            </thead>
                            <tbody>
                                {newEntries.map((entry) => (
                                    <tr key={entry.rowIndex} className="new-entry-row">
                                        <td>{entry.rowIndex + 1}</td>
                                        <td>{entry.excelRow['[Club] Name'] || '-'}</td>
                                        <td>{entry.excelRow['[Club] Subdomain'] || '-'}</td>
                                        <td>{entry.rtNumber !== undefined ? entry.rtNumber : 'N/A'}</td>
                                        <td>{entry.excelRow['[Bank Account] IBAN'] || '-'}</td>
                                        <td>{entry.excelRow['[Bank Account] BIC'] || '-'}</td>
                                        <td>{entry.excelRow['[Treasurer] Email'] || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Deviating Entries Section */}
            {deviatingEntries.length > 0 && (
                <div className="comparison-section">
                    <div className="comparison-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h4 className="comparison-section-title" style={{ margin: 0 }}>Abweichende Einträge ({deviatingEntries.length})</h4>
                        <button 
                            className="accept-deviating-entries-btn"
                            onClick={handleAcceptDeviatingEntries}
                            disabled={isAccepting}
                            style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: '#2196F3',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: isAccepting ? 'not-allowed' : 'pointer',
                                fontWeight: 'bold',
                                opacity: isAccepting ? 0.6 : 1,
                            }}
                        >
                            {isAccepting ? 'Wird übernommen...' : 'Änderungen übernehmen'}
                        </button>
                    </div>
                    {deviatingEntries.map((entry) => {
                        // Group deviations by category for better readability
                        const accountDeviations = entry.deviations?.filter(d => 
                            d.field.includes('[Bank Account]') || d.field.includes('[FinOrg]')
                        ) || [];
                        const addressDeviations = entry.deviations?.filter(d => 
                            d.field.includes('[Treasurer]')
                        ) || [];
                        const generalDeviations = entry.deviations?.filter(d => 
                            d.field.includes('[Club]')
                        ) || [];

                        return (
                            <div key={entry.rowIndex} className="deviating-entry">
                                <div className="deviating-entry-header">
                                    <strong>Zeile {entry.rowIndex + 1}</strong>
                                    <span> - </span>
                                    <span>{entry.excelRow['[Club] Name'] || 'Unbekannt'}</span>
                                    <span> (RT-Nummer: {entry.rtNumber})</span>
                                </div>
                                
                                {/* General Information Deviations */}
                                {generalDeviations.length > 0 && (
                                    <div style={{ marginTop: '1rem' }}>
                                        <h5 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem', color: '#555' }}>
                                            Allgemeine Informationen
                                        </h5>
                                        <div className="table-container">
                                            <table className="data-table deviations-table">
                                                <thead>
                                                    <tr>
                                                        <th>Feld</th>
                                                        <th>Excel-Wert</th>
                                                        <th>Aktueller Wert</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {generalDeviations.map((deviation, idx) => (
                                                        <tr key={idx} className="deviation-row">
                                                            <td className="field-name">{deviation.field}</td>
                                                            <td className="excel-value">
                                                                {deviation.excelValue !== null && deviation.excelValue !== undefined
                                                                    ? String(deviation.excelValue)
                                                                    : '-'}
                                                            </td>
                                                            <td className="current-value">
                                                                {deviation.currentValue !== null && deviation.currentValue !== undefined
                                                                    ? String(deviation.currentValue)
                                                                    : '-'}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* Bank Account Deviations */}
                                {accountDeviations.length > 0 && (
                                    <div style={{ marginTop: '1rem' }}>
                                        <h5 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem', color: '#555' }}>
                                            Kontoinformationen
                                        </h5>
                                        <div className="table-container">
                                            <table className="data-table deviations-table">
                                                <thead>
                                                    <tr>
                                                        <th>Feld</th>
                                                        <th>Excel-Wert</th>
                                                        <th>Aktueller Wert</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {accountDeviations.map((deviation, idx) => (
                                                        <tr key={idx} className="deviation-row">
                                                            <td className="field-name">{deviation.field}</td>
                                                            <td className="excel-value">
                                                                {deviation.excelValue !== null && deviation.excelValue !== undefined
                                                                    ? String(deviation.excelValue)
                                                                    : '-'}
                                                            </td>
                                                            <td className="current-value">
                                                                {deviation.currentValue !== null && deviation.currentValue !== undefined
                                                                    ? String(deviation.currentValue)
                                                                    : '-'}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* Address/Treasurer Deviations */}
                                {addressDeviations.length > 0 && (
                                    <div style={{ marginTop: '1rem' }}>
                                        <h5 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem', color: '#555' }}>
                                            Rechnungsadresse / Schatzmeister
                                        </h5>
                                        <div className="table-container">
                                            <table className="data-table deviations-table">
                                                <thead>
                                                    <tr>
                                                        <th>Feld</th>
                                                        <th>Excel-Wert</th>
                                                        <th>Aktueller Wert</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {addressDeviations.map((deviation, idx) => (
                                                        <tr key={idx} className="deviation-row">
                                                            <td className="field-name">{deviation.field}</td>
                                                            <td className="excel-value">
                                                                {deviation.excelValue !== null && deviation.excelValue !== undefined
                                                                    ? String(deviation.excelValue)
                                                                    : '-'}
                                                            </td>
                                                            <td className="current-value">
                                                                {deviation.currentValue !== null && deviation.currentValue !== undefined
                                                                    ? String(deviation.currentValue)
                                                                    : '-'}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
        </>
    );
};

export default DataComparisonCard;
